import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Database, ExternalLink, ArrowLeft, Shield, Globe, RefreshCw } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import LanguageSelector from '@/components/LanguageSelector';
import euSearchIcon from "@/assets/eu-search-icon.png";

const FonteDeiDati = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead
        title="Fonte dei Dati | Bandi di Gara Europei - TED Monitor"
        description="TED Monitor utilizza esclusivamente dati ufficiali provenienti da Tenders Electronic Daily (TED), la piattaforma ufficiale dell'Unione Europea per gli appalti pubblici."
        path="/it/fonte-dei-dati"
        alternates={[
          { lang: 'en', href: '/data-source' },
          { lang: 'it', href: '/it/fonte-dei-dati' }
        ]}
      />
      
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b bg-card/80 backdrop-blur-md sticky top-0 z-50">
          <div className="container mx-auto px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2 group cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md shadow-primary/25 group-hover:shadow-primary/40 transition-shadow overflow-hidden">
                <img src={euSearchIcon} alt="EU Search" className="w-6 h-6 object-contain" />
              </div>
              <span className="text-base font-bold text-foreground">TED Monitor</span>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSelector />
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="h-8 text-xs">
                <ArrowLeft className="w-3 h-3 mr-1" />
                Indietro
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-4 py-12 max-w-3xl">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 text-primary mb-4">
              <Database className="w-8 h-8" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              Fonte dei Dati Ufficiale
            </h1>
            <p className="text-muted-foreground">
              Trasparenza e affidabilità dei dati sugli appalti pubblici europei
            </p>
          </div>

          {/* Main Card */}
          <Card className="border-border/50 bg-card/50 backdrop-blur-sm mb-6">
            <CardContent className="p-6 md:p-8">
              <div className="space-y-6">
                {/* Data Origin Section */}
                <div>
                  <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-primary" />
                    Origine dei Dati
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">
                    TED Monitor si basa esclusivamente sui dati pubblicati su <strong>Tenders Electronic Daily (TED)</strong>, 
                    la piattaforma ufficiale dell'Unione Europea per la pubblicazione di bandi di gara pubblici. 
                    TED è gestito dall'Ufficio delle pubblicazioni dell'Unione europea e rappresenta la fonte 
                    più autorevole e completa per gli appalti pubblici europei sopra le soglie comunitarie.
                  </p>
                </div>

                {/* Update Frequency Section */}
                <div className="border-t border-border/50 pt-6">
                  <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 text-primary" />
                    Aggiornamento dei Dati
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">
                    I dati vengono sincronizzati quotidianamente con la banca dati TED, garantendo l'accesso 
                    alle informazioni più recenti sui bandi di gara. La nostra piattaforma elabora e indicizza 
                    automaticamente le nuove pubblicazioni per consentire ricerche rapide e precise.
                  </p>
                </div>

                {/* Independence Section */}
                <div className="border-t border-border/50 pt-6">
                  <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    Indipendenza del Servizio
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">
                    TED Monitor è un servizio indipendente e non è affiliato, sponsorizzato o approvato 
                    dall'Unione Europea o dall'Ufficio delle pubblicazioni. Forniamo strumenti avanzati di 
                    ricerca, monitoraggio e analisi per facilitare l'accesso alle opportunità di appalto, 
                    ma la fonte primaria dei dati rimane sempre la piattaforma TED ufficiale.
                  </p>
                </div>

                {/* Official Source Link */}
                <div className="border-t border-border/50 pt-6">
                  <p className="text-sm text-muted-foreground mb-3">
                    Consulta la fonte ufficiale dei dati:
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => window.open('https://ted.europa.eu', '_blank')}
                    className="gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    ted.europa.eu
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Disclaimer */}
          <p className="text-xs text-muted-foreground text-center">
            TED Monitor non è responsabile per eventuali errori o omissioni nei dati provenienti dalla fonte originale. 
            Si consiglia di verificare sempre le informazioni sulla piattaforma TED ufficiale prima di procedere con qualsiasi azione.
          </p>
        </main>
      </div>
    </>
  );
};

export default FonteDeiDati;
